import React, { useEffect, useRef, useState } from 'react';
import logoUrl from './assets/storsafe-logo.png';
import { supabase } from './supabaseClient';

const POLL_INTERVAL_MS = 5000;
const TIMEOUT_MS = 120000;

export default function PaymentProcessing() {
  const [timedOut, setTimedOut] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    let intervalId: number;

    async function checkSubscription() {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!sessionData.session) {
        setSessionExpired(true);
        window.clearInterval(intervalId);
        return;
      }
      const userId = sessionData.session.user.id;

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .single();
      if (cancelled || !profile?.company_id) return;

      const { data: company } = await supabase
        .from('companies')
        .select('subscription_status, current_period_end')
        .eq('id', profile.company_id)
        .single();
      if (cancelled) return;

      const isActive =
        company?.subscription_status === 'active' &&
        !!company.current_period_end &&
        new Date(company.current_period_end).getTime() > Date.now();

      if (isActive) {
        window.location.href = '/?welcome=true';
        return;
      }

      if (Date.now() - startedAtRef.current >= TIMEOUT_MS) {
        setTimedOut(true);
        window.clearInterval(intervalId);
      }
    }

    checkSubscription();
    intervalId = window.setInterval(checkSubscription, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div style={styles.root}>
      <img src={logoUrl} alt="StorSafe" style={styles.logo} />

      {sessionExpired ? (
        <>
          <div style={styles.clockIcon}>⚠️</div>
          <h1 style={styles.title}>Tu sesión expiró</h1>
          <p style={styles.sub}>
            Tu sesión expiró pero tu pago puede haberse procesado correctamente. Por favor inicia sesión para verificar el estado de tu cuenta.
          </p>
          <a href="/" style={styles.button}>Iniciar sesión</a>
        </>
      ) : timedOut ? (
        <>
          <div style={styles.clockIcon}>⏱</div>
          <h1 style={styles.title}>Tu pago está siendo verificado</h1>
          <p style={styles.sub}>
            Recibirás un email de confirmación cuando se active tu plan.
          </p>
          <a href="/" style={styles.button}>Ir al dashboard</a>
        </>
      ) : (
        <>
          <div style={styles.spinner} />
          <h1 style={styles.title}>Estamos confirmando tu pago...</h1>
          <p style={styles.sub}>Esto toma unos segundos</p>
        </>
      )}

      <style>{`@keyframes ss-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    padding: 24,
    textAlign: 'center',
    background: 'linear-gradient(160deg, #0a1628 0%, #0f172a 40%, #0d1f35 100%)',
    color: '#e2e8f0',
  },
  logo: { height: 120, marginBottom: 24 },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255,255,255,0.15)',
    borderTopColor: '#2FBF71',
    borderRadius: '50%',
    animation: 'ss-spin 0.8s linear infinite',
  },
  clockIcon: { fontSize: '2.2rem' },
  title: { fontSize: '1.5rem', fontWeight: 800, color: '#f1f5f9', margin: 0 },
  sub: { fontSize: '0.95rem', color: '#94a3b8', maxWidth: 420, lineHeight: 1.6, margin: 0 },
  button: {
    marginTop: 8,
    padding: '12px 28px',
    background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)',
    color: '#fff',
    fontWeight: 700,
    borderRadius: 10,
    textDecoration: 'none',
    fontSize: '0.9rem',
  },
};
